/**
 * The feedback page's blocks and notes, built from the response (#661), and the arithmetic that places them. Pure: no React, no DOM.
 *
 * **One function builds the notes** (`buildBoardNotes`), so that when the Checker session's response carries its own block notes
 * (`notes: [{ id, questionLabel, questionText?, kind, page, blockBbox, regionIds, title, detail?, courseSource?, rungs,
 * policyVersions }]`) the swap is this function and nothing else. Today a response note is per line or per claim, and
 * the page shows each as a block with one note.
 *
 * **Nothing here decides a verdict.** A block is ✓ or ✗ only when the response's own mark says so (`proved`/`ruled_correct`,
 * `refuted`); every other mark is a neutral block, whatever its colour would suggest. The one thing done to the response is a
 * display merge: consecutive `read_not_checked` lines on a page become one neutral block, which adds nothing the lines did not say
 * and removes nothing from them (every merged note's own text and readings stay in the note it was folded into). A `refused`,
 * `undecided`, `withheld`, `under_review` or closed note is never merged: each says something of its own.
 *
 * The question strip names a question from the response's own label (`questionGroup`); the response has no question text today,
 * so `questionText` is carried only when it exists.
 */

import type { HomeworkFeedbackChannelResponse } from '@davinci/contracts';
import type { FeedbackNote } from './feedback-model.js';

export interface Bbox {
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
}

export type ChannelFlag = HomeworkFeedbackChannelResponse['flags'][number];

/** The four colours of the board, plus a line under review (a neutral block with its own mark). */
export type BoardKind = 'right' | 'wrong' | 'look' | 'unchecked' | 'review';

export interface BoardNote {
  /** Stable across renders: the claim id, or the lines' own ids. */
  readonly id: string;
  /** The pin's number, in reading order over the whole sheet. A name, not a count. */
  readonly number: number;
  readonly kind: BoardKind;
  /** The response's own mark, or `check_this` for a line the reviewer's channel pointed at. */
  readonly mark: FeedbackNote['mark'] | 'check_this';
  readonly pageId: string;
  readonly questionLabel: string | null;
  /** The question is DaVinci's display-only guess (#646, `questionHint`), not a recorded label. Shown quietly as such (#668). */
  readonly questionIsGuess?: boolean;
  /** The question's own text, when a response carries it. Never invented. */
  readonly questionText?: string;
  /** Where in the course a rule is from, when a response carries it ready made. Otherwise the card reads the rules. */
  readonly courseSource?: string;
  readonly title?: string;
  readonly detail?: string;
  /** The block's frame in the page image's own pixels (the union of its lines' boxes), or null: the note quotes its line instead. */
  readonly blockBbox: Bbox | null;
  readonly regionIds: readonly string[];
  /** The response notes this one was built from: one, or several `read_not_checked` lines folded together. Empty for a channel flag. */
  readonly members: readonly FeedbackNote[];
  readonly flag?: ChannelFlag;
  /** The reading of a flagged line, when a response note carries one for that region. The channel's flag does not carry text of its own. */
  readonly flagReading?: { readonly regionId: string; readonly text: string; readonly learnerCorrected: boolean };
  /** Several `read_not_checked` lines folded into one block. */
  readonly merged: boolean;
  /** A later summary on the same page: only its title and its "how this was checked" are drawn. */
  readonly repeat: boolean;
}

const NOT_CHECKED_MERGEABLE: FeedbackNote['mark'] = 'read_not_checked';

export function kindOf(mark: FeedbackNote['mark']): BoardKind {
  switch (mark) {
    case 'proved':
    case 'ruled_correct':
      return 'right';
    case 'refuted':
      return 'wrong';
    case 'under_review':
      return 'review';
    default:
      return 'unchecked';
  }
}

export function noteId(note: FeedbackNote): string {
  return note.claimId ?? `${note.pageId}-${note.regionIds.join('-')}`;
}

export function frameOf(note: FeedbackNote): Bbox | null {
  return note.location?.kind === 'frame' && note.location.bbox !== undefined ? note.location.bbox : null;
}

export function unionBbox(boxes: readonly Bbox[]): Bbox | null {
  const [first, ...rest] = boxes;
  if (first === undefined) return null;
  return rest.reduce<Bbox>(
    (all, box) => ({ x0: Math.min(all.x0, box.x0), y0: Math.min(all.y0, box.y0), x1: Math.max(all.x1, box.x1), y1: Math.max(all.y1, box.y1) }),
    first,
  );
}

/**
 * The question a label belongs to, for the strip: the label's leading number when something follows it ("2b" is in question 2),
 * else the label whole. A label that starts with no digit is its own question. Null for no label.
 */
export function questionGroup(label: string | null): string | null {
  if (label === null) return null;
  const lead = /^\d+/.exec(label)?.[0];
  return lead !== undefined && lead.length < label.length ? lead : label;
}

export interface BuildInput {
  readonly notes: readonly FeedbackNote[];
  /** The lines the reviewer's channel pointed at (`check_this`), as the open call showed them. */
  readonly flags: readonly ChannelFlag[];
  /** The 1-based page number of a page id; flags and notes on an unknown page are left out. */
  readonly pageNumberOf: (pageId: string) => number;
  /** Which page ids exist. */
  readonly hasPage: (pageId: string) => boolean;
}

interface Draft extends Omit<BoardNote, 'number' | 'repeat'> {
  readonly order: number;
}

/** A block that is not on its page (no frame) sorts after the ones that are. */
const AFTER = Number.POSITIVE_INFINITY;

export function buildBoardNotes(input: BuildInput): BoardNote[] {
  const drafts: Draft[] = [];
  const shown = input.notes.filter((note) => note.regionIds.length > 0);

  // Runs of consecutive read_not_checked lines, per page, in the page's own top-to-bottom order.
  const byPage = new Map<string, { note: FeedbackNote; index: number }[]>();
  shown.forEach((note, index) => byPage.set(note.pageId, [...(byPage.get(note.pageId) ?? []), { note, index }]));
  const folded = new Set<FeedbackNote>();
  for (const [, entries] of byPage) {
    const framed = entries
      .filter((entry) => frameOf(entry.note) !== null)
      .sort((a, b) => (frameOf(a.note)?.y0 ?? 0) - (frameOf(b.note)?.y0 ?? 0) || (frameOf(a.note)?.x0 ?? 0) - (frameOf(b.note)?.x0 ?? 0) || a.index - b.index);
    let run: { note: FeedbackNote; index: number }[] = [];
    const flush = (): void => {
      if (run.length === 0) return;
      const members = run.map((entry) => entry.note);
      const first = members[0] as FeedbackNote;
      for (const member of members) folded.add(member);
      const bbox = unionBbox(members.map((member) => frameOf(member) as Bbox));
      drafts.push({
        id: members.length === 1 ? noteId(first) : `unchecked-${noteId(first)}`,
        kind: 'unchecked',
        mark: NOT_CHECKED_MERGEABLE,
        pageId: first.pageId,
        questionLabel: first.questionLabel,
        ...(members.some((member) => member.questionHint !== undefined) ? { questionIsGuess: true } : {}),
        blockBbox: bbox,
        regionIds: members.flatMap((member) => member.regionIds),
        members,
        merged: members.length > 1,
        order: Math.min(...run.map((entry) => entry.index)),
      });
      run = [];
    };
    for (const entry of framed) {
      const sameRun = run.length > 0 && run[0]?.note.questionLabel === entry.note.questionLabel;
      if (entry.note.mark === NOT_CHECKED_MERGEABLE) {
        if (run.length > 0 && !sameRun) flush();
        run.push(entry);
      } else flush();
    }
    flush();
  }
  shown.forEach((note, index) => {
    if (folded.has(note)) return;
    drafts.push({
      id: noteId(note),
      kind: kindOf(note.mark),
      mark: note.mark,
      pageId: note.pageId,
      questionLabel: note.questionLabel,
      ...(note.questionHint === undefined ? {} : { questionIsGuess: true }),
      blockBbox: frameOf(note),
      regionIds: note.regionIds,
      members: [note],
      merged: false,
      order: index,
    });
  });

  // The reviewer's pointers: each a one-line look-here block of its own. A flag on no known page is left to the channel's section.
  const labelOfRegion = (regionId: string): string | null => input.notes.find((note) => note.regionIds.includes(regionId))?.questionLabel ?? null;
  const readingOfRegion = (regionId: string): { regionId: string; text: string; learnerCorrected: boolean } | undefined =>
    input.notes.flatMap((note) => note.readings).find((reading) => reading.regionId === regionId);
  input.flags.forEach((flag, index) => {
    if (flag.flag !== 'check_this' || flag.pageId === null || !input.hasPage(flag.pageId)) return;
    const flagReading = readingOfRegion(flag.regionId);
    drafts.push({
      id: `flag-${flag.regionId}`,
      kind: 'look',
      mark: 'check_this',
      pageId: flag.pageId,
      questionLabel: labelOfRegion(flag.regionId),
      ...(input.notes.find((note) => note.regionIds.includes(flag.regionId))?.questionHint === undefined ? {} : { questionIsGuess: true }),
      blockBbox: flag.location?.kind === 'frame' && flag.location.bbox !== undefined ? flag.location.bbox : null,
      regionIds: [flag.regionId],
      members: [],
      flag,
      ...(flagReading === undefined ? {} : { flagReading }),
      merged: false,
      order: shown.length + index,
    });
  });

  // Reading order: page, then top to bottom, then the response's own order. Then the numbers, and which summaries repeat.
  drafts.sort(
    (a, b) =>
      input.pageNumberOf(a.pageId) - input.pageNumberOf(b.pageId) ||
      (a.blockBbox?.y0 ?? AFTER) - (b.blockBbox?.y0 ?? AFTER) ||
      (a.blockBbox?.x0 ?? AFTER) - (b.blockBbox?.x0 ?? AFTER) ||
      a.order - b.order,
  );
  const seenSummary = new Set<string>();
  return drafts.map(({ order: _order, ...draft }, index) => {
    let repeat = false;
    if (draft.kind === 'unchecked' && draft.mark === NOT_CHECKED_MERGEABLE && draft.merged) {
      repeat = seenSummary.has(draft.pageId);
      seenSummary.add(draft.pageId);
    }
    return { ...draft, number: index + 1, repeat };
  });
}

/**
 * The notes that open a question on their page (`resetPerPage`) or in the whole reading (the phone's one stack): the strip is drawn
 * above these. A note with no label opens nothing.
 */
/** A line that was read and nothing more. `refused`, `undecided` and `withheld` each give a reason, so they are not this. */
export const onlyRead = (note: BoardNote): boolean => note.kind === 'unchecked' && note.mark === 'read_not_checked';

/** Whether any line shown under this question is placed there by DaVinci's guess. A hint carries forward, so a recorded line can come first. */
export function questionHasGuess(notes: readonly BoardNote[], label: string | null): boolean {
  const group = questionGroup(label);
  return notes.some((note) => note.questionIsGuess === true && questionGroup(note.questionLabel) === group);
}

export function stripStarts(notes: readonly BoardNote[], resetPerPage: boolean): ReadonlySet<string> {
  const starts = new Set<string>();
  let last: string | null = null;
  let lastPage: string | null = null;
  for (const note of notes) {
    if (resetPerPage && note.pageId !== lastPage) last = null;
    lastPage = note.pageId;
    const group = questionGroup(note.questionLabel);
    if (group !== null && group !== last) starts.add(note.id);
    if (group !== null) last = group;
  }
  return starts;
}

// ---------------------------------------------------------------------------------------------------------------------
// Where the notes sit in the gutter, and the line from a note to its frame.

export interface Placement {
  readonly id: string;
  /** Where the note would like to be: the top of its block, in the sheet's pixels. Null: it has no block, and goes below the others. */
  readonly desiredTop: number | null;
  /** The note's measured height. */
  readonly height: number;
}

/**
 * The top of each note, in the sheet's pixels: at its block's height, and a lower note moves down so that no two overlap, with
 * `gap` between them. Notes keep their order of `desiredTop` (ties keep the order given), so a note never jumps over another.
 */
export function layoutNotes(items: readonly Placement[], gap = 14): Map<string, number> {
  const last = Number.POSITIVE_INFINITY;
  const sorted = items.map((item, index) => ({ item, index })).sort((a, b) => (a.item.desiredTop ?? last) - (b.item.desiredTop ?? last) || a.index - b.index);
  const tops = new Map<string, number>();
  let bottom = Number.NEGATIVE_INFINITY;
  for (const { item } of sorted) {
    const top = Math.max(item.desiredTop ?? 0, bottom + gap);
    tops.set(item.id, top);
    bottom = top + item.height;
  }
  return tops;
}

/** The note gutter's width and the space between it and the page, in CSS pixels: the stylesheet's `--fb-gutter-w` and `--fb-gutter-gap`. */
export const GUTTER_WIDTH = 320;
export const GUTTER_GAP = 20;

/** The sheet's height that holds the page and every note. */
export function layoutHeight(items: readonly Placement[], tops: ReadonlyMap<string, number>, pageHeight: number, pad = 24): number {
  let bottom = pageHeight;
  for (const item of items) bottom = Math.max(bottom, (tops.get(item.id) ?? 0) + item.height + pad);
  return bottom;
}

export interface ConnectorInput {
  /** The gutter's own x of the note's near edge, and of the frame's near edge (both in the gutter's coordinates). */
  readonly noteEdgeX: number;
  readonly frameEdgeX: number;
  readonly noteTop: number;
  readonly frameTop: number;
  readonly frameHeight: number;
  /** Which connector this is, only to keep neighbouring bends from running on one line. */
  readonly index: number;
}

/** How far down a note its line leaves, and the most a frame's dot sits below the frame's top. */
const NOTE_ANCHOR = 26;
const FRAME_ANCHOR_MAX = 40;

export function connectorPath(c: ConnectorInput): { d: string; dot: { x: number; y: number } } {
  const fy = c.frameTop + Math.min(c.frameHeight / 2, FRAME_ANCHOR_MAX);
  const ny = c.noteTop + NOTE_ANCHOR;
  const dot = { x: c.frameEdgeX, y: fy };
  if (Math.abs(ny - fy) < 3) return { d: `M${n(c.noteEdgeX)} ${n(ny)} L${n(c.frameEdgeX)} ${n(fy)}`, dot };
  const mx = (c.noteEdgeX + c.frameEdgeX) / 2 + ((c.index % 3) - 1) * 6;
  return { d: `M${n(c.noteEdgeX)} ${n(ny)} L${n(mx)} ${n(ny)} L${n(mx)} ${n(fy)} L${n(c.frameEdgeX)} ${n(fy)}`, dot };
}

const n = (value: number): string => String(Math.round(value * 10) / 10);

export interface GutterGeometry {
  readonly gutterWidth: number;
  /** The space between the page and the gutter. */
  readonly gap: number;
  readonly pageWidth: number;
  readonly rtl: boolean;
}

/**
 * The near edges, in the gutter's own coordinates (its inline-start edge at 0): the note's, and the frame's side that faces the
 * gutter. The page is the image, so it is never mirrored: in a right-to-left sheet the gutter is on the left and a frame's left
 * edge is the one that faces it.
 */
export function edges(g: GutterGeometry, box: Bbox, scale: number): { noteEdgeX: number; frameEdgeX: number } {
  if (g.rtl) return { noteEdgeX: g.gutterWidth, frameEdgeX: g.gutterWidth + g.gap + box.x0 * scale };
  return { noteEdgeX: 0, frameEdgeX: box.x1 * scale - g.pageWidth - g.gap };
}
